import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

/*
 * GoogleOneTap
 * ------------
 * Renders Google's One Tap prompt in the standard top-right corner per Google's
 * own UX guidelines. Fires only when the user is signed OUT and the current
 * route is public-facing (home, /countries, /universities, /pricing).
 *
 * Configuration
 *   Set REACT_APP_GOOGLE_CLIENT_ID in /app/frontend/.env with the OAuth client
 *   ID from Google Cloud Console → Credentials → OAuth 2.0 Client IDs (Web app).
 *   Authorised JavaScript origins should include the preview URL and any custom
 *   production domain.
 *
 * Backend
 *   The credential JWT is POSTed to /api/auth/google which currently returns
 *   501 Not Implemented — swap that endpoint's body for the auth playbook's
 *   verification + user-upsert flow once the client ID is provisioned.
 */
export default function GoogleOneTap() {
  const { user } = useAuth();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    // Don't initialize when signed in or when no client id is configured yet.
    if (user) return;
    if (!clientId) return;
    if (typeof window === "undefined" || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async ({ credential }) => {
        try {
          const r = await api.post("/auth/google", { credential });
          // Expected shape (once backend verification is wired):
          //   { access_token, user }
          if (r?.data?.access_token) {
            localStorage.setItem("token", r.data.access_token);
            toast.success(`Welcome, ${r.data.user?.name || "back"}.`);
            window.location.reload();
          }
        } catch (e) {
          const status = e?.response?.status;
          if (status === 501) {
            toast.info("Google sign-in is not switched on yet.");
          } else {
            toast.error("Sign-in failed. Try again in a moment.");
          }
        }
      },
      auto_select: false,
      cancel_on_tap_outside: false,
    });

    // The prompt appears in the standard corner position per Google's guidelines.
    window.google.accounts.id.prompt();
  }, [user, clientId]);

  return null;
}
